import React, { useEffect, useState, useMemo } from "react";
import AppLayout from "../components/AppLayout";
import { api, errMsg, formatCurrency } from "../lib/api";
import { Plus, Trash2, TrendingUp, DollarSign, AlertTriangle, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { exportRollupPDF } from "../lib/rollupPdf";
import OwnerSelect from "../components/OwnerSelect";

export default function PortfoliosPage() {
  const [portfolios, setPortfolios] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pf, pg, s, tm] = await Promise.all([
        api.get("/portfolios"), api.get("/programs"), api.get("/analytics/project-summary"), api.get("/teammates"),
      ]);
      setPortfolios(pf.data); setPrograms(pg.data); setSummaries(s.data); setTeammates(tm.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      const { data } = await api.post("/portfolios", { data: { name: "New Portfolio", owner: "", description: "", budget: 0, selected_programs: [] }});
      setPortfolios((x) => [data, ...x]);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const update = async (id, patch) => {
    setPortfolios((xs) => xs.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    try { await api.put(`/portfolios/${id}`, { data: patch }); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete portfolio?")) return;
    try { await api.delete(`/portfolios/${id}`); setPortfolios((xs) => xs.filter((p) => p.id !== id)); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleProgram = (portfolioId, programId, checked) => {
    const p = portfolios.find((x) => x.id === portfolioId);
    const next = checked ? [...(p.selected_programs || []), programId] : (p.selected_programs || []).filter((x) => x !== programId);
    update(portfolioId, { selected_programs: next });
  };

  const downloadRollup = async (id, name) => {
    try {
      toast.message(`Building ${name} rollup…`);
      const { data } = await api.get(`/analytics/rollup/portfolio/${id}`);
      exportRollupPDF(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rollup = useMemo(() => (selectedProgramIds = []) => {
    const projects = summaries.filter((p) => selectedProgramIds.includes(p.program_id));
    const est = projects.reduce((a, p) => a + p.estimated_cost, 0);
    const actual = projects.reduce((a, p) => a + p.actual_cost, 0);
    return { est, actual, projectCount: projects.length };
  }, [summaries]);

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Portfolios</h1>
          <p className="text-slate-400 mt-1">Top-level view. Group programs into portfolios — totals roll up live.</p></div>
        <button onClick={add} data-testid="add-portfolio-btn" className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"><Plus size={14}/> New portfolio</button>
      </div>
      {loading ? <div className="text-slate-400">Loading…</div>
        : portfolios.length === 0 ? <div className="pc-card p-10 text-center text-slate-400">No portfolios yet.</div>
        : <div className="space-y-4" data-testid="portfolios-list">
            {portfolios.map((p) => {
              const r = rollup(p.selected_programs || []);
              const budget = Number(p.budget || 0);
              const estVar = r.est - budget;
              const actualVar = r.actual - budget;
              const overBudget = actualVar > 0;
              return (
                <div key={p.id} className="pc-card p-5" data-testid={`portfolio-${p.id}`}>
                  <div className="flex gap-3 items-start mb-3">
                    <input value={p.name || ""} onChange={(e) => update(p.id, { name: e.target.value })} className="flex-1 text-lg font-semibold text-white bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 outline-none" style={{ fontFamily: "Outfit" }}/>
                    <button onClick={() => downloadRollup(p.id, p.name)} data-testid={`portfolio-rollup-pdf-${p.id}`} className="inline-flex items-center gap-1 border border-slate-700 hover:border-blue-500 text-slate-200 px-3 py-1.5 rounded-lg text-xs"><FileDown size={12}/> Rollup PDF</button>
                    <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-rose-400"><Trash2 size={14}/></button>
                  </div>
                  <div className="grid sm:grid-cols-4 gap-3 mb-3">
                    <OwnerSelect teammates={teammates} value={p.owner_teammate_id} legacy={p.owner} testId={`portfolio-owner-${p.id}`}
                      onChange={(v) => update(p.id, { owner_teammate_id: v, owner: teammates.find((t) => t.id === v)?.name || "" })} />
                    <input placeholder="Description" value={p.description || ""} onChange={(e) => update(p.id, { description: e.target.value })} className="sm:col-span-2 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
                    <input type="number" placeholder="Budget" value={p.budget || 0} onChange={(e) => update(p.id, { budget: Number(e.target.value || 0) })} className="px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid={`portfolio-rollup-${p.id}`}>
                    <Card label="Budget" value={formatCurrency(budget)} icon={DollarSign} />
                    <Card label="Cost Est" value={formatCurrency(r.est)} sub={estVar === 0 ? "On budget" : estVar > 0 ? `${formatCurrency(estVar)} over` : `${formatCurrency(Math.abs(estVar))} under`} icon={TrendingUp} tone={estVar > 0 ? "rose" : "emerald"} />
                    <Card label="Actual Cost" value={formatCurrency(r.actual)} sub={budget === 0 ? `${r.projectCount} projects` : overBudget ? `${formatCurrency(actualVar)} over` : `${formatCurrency(Math.abs(actualVar))} under`} icon={overBudget ? AlertTriangle : CheckCircle2} tone={overBudget ? "rose" : "emerald"} />
                    <Card label="Projects" value={r.projectCount} sub={`across ${(p.selected_programs || []).length} programs`} icon={DollarSign} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Include programs</div>
                    <div className="flex flex-wrap gap-2">
                      {programs.length === 0 ? <span className="text-slate-500 text-sm">Create a program first</span>
                        : programs.map((pg) => {
                          const checked = (p.selected_programs || []).includes(pg.id);
                          return (
                            <label key={pg.id} className={`cursor-pointer px-3 py-1.5 rounded-lg border text-sm ${checked ? "border-blue-500 bg-blue-500/10 text-blue-300" : "border-slate-700 text-slate-300"}`}>
                              <input type="checkbox" className="hidden" checked={checked} onChange={(e) => toggleProgram(p.id, pg.id, e.target.checked)}/>
                              {pg.name}
                            </label>
                          );
                        })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}
    </AppLayout>
  );
}

function Card({ label, value, sub, icon: Icon, tone }) {
  const toneCls = tone === "rose" ? "text-rose-300" : tone === "emerald" ? "text-emerald-300" : "text-white";
  return (
    <div className="rounded-lg bg-slate-900/40 border border-slate-800 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-blue-300/80">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className={`text-lg font-semibold mt-1 ${toneCls}`} style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
